/**
 * ⭐⭐ THE CUSTOMER ACCOUNT BOOK'S FOUR READS, LIFTED OUT OF `Admin.tsx` (T1749
 * M7, R5), BECAUSE TWO HOSTS NOW MOUNT THE SAME BOOK.
 *
 * R5 moves the account book under the Nursing homes tab, so its lists are
 * rendered from `admin/NursingHomes.tsx` as well as from `screens/Admin.tsx`.
 * Two copies of a four-read load is two places to get the degrade rules wrong,
 * and those rules are the whole content of this function.
 *
 * ⛔⛔ IT RETURNS A NAMED OBJECT, AND THAT IS THE POINT OF LIFTING IT RATHER THAN
 * COPYING IT. `Admin.tsx` destructured a SEVEN-ENTRY `Promise.all` BY POSITION,
 * and its own comment recorded the hazard in as many words: appending a fetch in
 * one milestone and another in a second silently binds `accounts` to the
 * destinations array, and because both row shapes are `{id, name, active}`
 * TypeScript compiles and the suite passes. A second host destructuring the same
 * array would have doubled that surface. Here the four reads are settled
 * together and handed back BY NAME, so a fifth read added later cannot shift
 * anything.
 * ⚠️ THE POSITIONAL PIN IN `NursingHomes.tsx` IS NOT RETIRED BY THIS. That guard
 * watches the ROSTER's four-read array, which is still positional and still
 * genuinely distinguishable only by position; this function removes the account
 * book's array from that class, not the roster's.
 *
 * ⛔ ALL FOUR READS DEGRADE TO `null`, NOT TO AN EMPTY LIST, AND THE DISTINCTION
 * IS THE WHOLE DESIGN (T698). An empty list had two causes and one appearance,
 * so a read that FAILED rendered as "nothing captured yet" — and an operator who
 * concludes the rows are gone is one step from re-adding what is already there,
 * which splits one place into several rate keys (`docs/pricing-spec.md`). The
 * `failed` flags below are what let `AccountBook` say which of the two happened.
 *
 * ⛔ AND THEY DEGRADE AT ALL, unlike the rates read `Admin.tsx` keeps beside
 * them: left unwrapped, a failing accounts read would put the whole price book
 * into its error state, which inverts the reason the book has a quiet entry —
 * a section has to stay fixable on a screen whose other read is broken. On the
 * Nursing homes host the same rule reads the other way round: a dead accounts
 * route must not take the roster down with it.
 *
 * ⚠️ A FAILED READ IS NOT A TRUNCATED ONE. `?? false` on the truncation flags is
 * deliberate: an outage is not a bound, and claiming otherwise would put a "some
 * rows are hidden" line under a list whose emptiness has an entirely different
 * cause. The failure flag beside it is what says the true thing instead.
 */
import {
  listAccounts,
  listDestinations,
  listAccountPrices,
  listBillingParties,
  type ApiAccount,
  type ApiDestination,
  type ApiAccountPrice,
  type ApiBillingParty,
} from './actions.js';
import { loadMeta } from '../store/registry.js';
import type { BookTruncation, BookFailure } from '../screens/admin/AccountBook.js';

/**
 * Everything `AccountBook` needs, in one snapshot.
 *
 * ⛔ ONE OBJECT, WRITTEN OR NOT AT ALL, and that is an invariant rather than a
 * style. The rows, the truncation flags and the failure flags belong together:
 * `DestinationList`'s two-arm empty copy depends on it silently ("every
 * destination is out of use" is only reachable when the list is non-empty), so
 * rows from load B rendered against flags from load A would resurrect exactly
 * the ambiguity the flags remove. A host must never split this into two states.
 */
export type AccountBookData = {
  accounts: ApiAccount[];
  destinations: ApiDestination[];
  prices: ApiAccountPrice[];
  billingParties: ApiBillingParty[];
  truncated: BookTruncation;
  failed: BookFailure;
  /**
   * ⭐⭐ T1749 M7 wave-3 — `app_meta`, READ ON THE SAME REFRESH AS THE FOUR LISTS,
   * and it is in this snapshot for one reason: the contract `PriceList.tsx`
   * states at its `sendToPortal` call site. "WITHOUT THIS THE VERDICT LINE STAYS
   * STALE UNTIL THE VIEW IS RE-ENTERED: `meta` refreshes only through
   * `onChanged -> Admin.load -> loadMeta`." When the book moved hosts its
   * `onChanged` re-ran the four reads and nothing else, so the "Sent to the
   * portal." / "Not sent to the portal yet." verdict was frozen for the whole
   * session — measured by the output gate: `/api/options` was never called on
   * that host, before or after a send.
   * ⇒ the read rides HERE, so one refresh carries the rows and the verdict and a
   * host cannot get the pair half right.
   * ⛔ `null` MEANS THE READ HAS NOT SETTLED OR FAILED, which is a distinct value
   * from an empty map, exactly as the four `failed` flags are distinct from an
   * empty list: an empty map would tell Helen every account is "Not sent to the
   * portal yet." on the strength of a read that never answered.
   */
  meta: Record<string, string> | null;
  /** False until the FIRST load settles. `EMPTY_ACCOUNT_BOOK` is
   *  indistinguishable from a real empty book, so the sub-second before the
   *  first resolve would otherwise read as "nothing captured yet" too. */
  loaded: boolean;
};

const NOTHING: Readonly<Record<'accounts' | 'destinations' | 'prices' | 'billingParties', false>> = {
  accounts: false, destinations: false, prices: false, billingParties: false,
};

/** The pre-first-load snapshot. `loaded: false` is what tells the book apart
 *  from a real empty one. */
export const EMPTY_ACCOUNT_BOOK: AccountBookData = {
  accounts: [], destinations: [], prices: [], billingParties: [],
  truncated: { ...NOTHING }, failed: { ...NOTHING }, meta: null, loaded: false,
};

/**
 * Read the four lists and settle them into one named snapshot.
 *
 * ⛔ IT NEVER REJECTS. Every read is caught here, so a host may `void` it or
 * `.then` it without a second failure path of its own — which is what makes the
 * two hosts' degrade behaviour identical by construction rather than by two
 * comments agreeing with each other.
 */
export async function loadAccountBook(): Promise<AccountBookData> {
  const [accounts, destinations, prices, billingParties, meta] = await Promise.all([
    listAccounts().catch(() => null),
    listDestinations().catch(() => null),
    listAccountPrices().catch(() => null),
    listBillingParties().catch(() => null),
    // ⭐ THE FIFTH READ IS THE VERDICT'S, and it degrades the same way: `null`
    // when it did not answer, never an empty map.
    loadMeta().catch(() => null),
  ]);
  return {
    accounts: accounts?.rows ?? [],
    destinations: destinations?.rows ?? [],
    prices: prices?.rows ?? [],
    billingParties: billingParties?.rows ?? [],
    truncated: {
      accounts: accounts?.truncated ?? false,
      destinations: destinations?.truncated ?? false,
      prices: prices?.truncated ?? false,
      billingParties: billingParties?.truncated ?? false,
    },
    failed: {
      accounts: accounts == null,
      destinations: destinations == null,
      prices: prices == null,
      billingParties: billingParties == null,
    },
    meta: meta ?? null,
    loaded: true,
  };
}
